import React, { useMemo, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BlockRenderer from "../components/BlockRenderer";
import ItemCard from "../components/ItemCard";
import {
  CrumbLine,
  CtaPair,
  EnquiryRail,
  HeroBand,
  SectionHead,
  SiblingPills,
  StatChip,
  useEnquiry,
} from "./parts";

/**
 * LISTING page - multi-brand product index.
 * Dense grid with live filtering, sorting and a brand rail, because these
 * pages carry the largest item counts on the site.
 */
export default function ListingTemplate({
  page,
  items = [],
  siblings = [],
  breadcrumbs = [],
  parentLabel,
  phone,
}) {
  const heroImg = page.hero_image || page.thumb_image;
  const to = useEnquiry(page, heroImg, parentLabel);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("all");
  const [sort, setSort] = useState("default");

  const brands = useMemo(
    () => Array.from(new Set(items.map((i) => i.brand).filter(Boolean))).sort(),
    [items]
  );

  const visible = useMemo(() => {
    let out = items;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((i) => (i.name || "").toLowerCase().includes(needle));
    }
    if (brand !== "all") out = out.filter((i) => i.brand === brand);
    if (sort === "az") out = [...out].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (sort === "za") out = [...out].sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    return out;
  }, [items, q, brand, sort]);

  const textBlocks = (page.blocks || []).filter((b) => b.type !== "image");
  const heading = page.h1 && page.h1.length < 90 ? page.h1 : page.title;
  const filtered = q.trim() || brand !== "all";

  return (
    <div data-testid="template-listing">
      <HeroBand image={heroImg} dim={0.2}>
        <CrumbLine breadcrumbs={breadcrumbs} parentLabel={parentLabel} />
        <div className="mt-2 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <h1
              className="text-3xl font-bold leading-tight text-[color:var(--nq-sand)] md:text-5xl"
              data-testid="page-title"
            >
              {heading}
            </h1>
            {page.meta_description && (
              <p className="mt-4 text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-base">
                {page.meta_description}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <StatChip value={items.length} label="Products" />
            {brands.length > 0 && <StatChip value={brands.length} label="Brands" />}
          </div>
        </div>
        <div className="mt-7">
          <CtaPair to={to} label="Enquire now" phone={phone} />
        </div>
      </HeroBand>

      <div className="container-nq section-nq">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-9">
            {/* toolbar */}
            <div
              className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border p-3"
              style={{ borderColor: "rgba(255,255,255,0.08)", background: "var(--nq-ink)" }}
              data-testid="listing-toolbar"
            >
              <div className="relative min-w-[200px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--nq-stone)]" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={`Search ${items.length} products`}
                  className="pl-9"
                  aria-label="Search products on this page"
                  data-testid="listing-search-input"
                />
              </div>

              {brands.length > 1 && (
                <Select value={brand} onValueChange={setBrand}>
                  <SelectTrigger className="w-[170px]" data-testid="listing-brand-select">
                    <SelectValue placeholder="All brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All brands</SelectItem>
                    {brands.map((b) => (
                      <SelectItem key={b} value={b}>
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[150px]" data-testid="listing-sort-select">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Featured</SelectItem>
                  <SelectItem value="az">Name A-Z</SelectItem>
                  <SelectItem value="za">Name Z-A</SelectItem>
                </SelectContent>
              </Select>

              {filtered && (
                <button
                  type="button"
                  onClick={() => {
                    setQ("");
                    setBrand("all");
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-[color:var(--nq-stone)] transition-colors duration-200 hover:text-[color:var(--nq-gold)]"
                  data-testid="listing-clear-filters"
                >
                  <X className="h-3.5 w-3.5" /> Clear
                </button>
              )}
            </div>

            <div className="mb-5 flex items-center gap-2 text-xs text-[color:var(--nq-stone)]">
              <SlidersHorizontal className="h-3.5 w-3.5 gold-text" />
              <span data-testid="listing-result-count">
                Showing {visible.length} of {items.length}
              </span>
            </div>

            {visible.length > 0 ? (
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="page-items-grid">
                {visible.map((it) => (
                  <ItemCard key={it.id} item={it} pageTitle={page.title} />
                ))}
              </div>
            ) : (
              <div className="card-nq p-10 text-center" data-testid="listing-empty-state">
                <p className="text-sm text-[color:var(--nq-stone)]">
                  Nothing matches that search on this page.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setQ("");
                    setBrand("all");
                  }}
                  className="btn-outline-nq mt-4"
                  data-testid="listing-empty-reset"
                >
                  Reset filters
                </button>
              </div>
            )}

            {textBlocks.length > 0 && (
              <section className="mt-14">
                <SectionHead eyebrow="About this range" title={page.title} />
                <BlockRenderer blocks={textBlocks} />
              </section>
            )}
          </div>

          <aside className="lg:col-span-3">
            <EnquiryRail page={page} to={to} phone={phone} siblings={siblings} />
          </aside>
        </div>

        <div className="mt-14">
          <SiblingPills siblings={siblings} />
        </div>
      </div>
    </div>
  );
}
